import type { BaseComponentProps } from '@json-render/react';
import { useBoundProp } from '@json-render/react';
import type { ShadcnProps } from '@json-render/shadcn/catalog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from 'components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import { Label } from 'components/ui/label';
import { Progress } from 'components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Slider } from 'components/ui/slider';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'components/ui/tabs';
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'components/ui/toggle-group';
import constant from 'lodash/constant';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import map from 'lodash/map';
import replace from 'lodash/replace';
import sortBy from 'lodash/sortBy';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import {
  Area,
  AreaChart as RechartsAreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Sankey as RechartsSankey,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { z } from 'zod';

import type {
  AreaChartProps as AreaChartSchema,
  BarChartProps as BarChartSchema,
  CardProps as CardSchema,
  ComboChartProps as ComboChartSchema,
  DataTableProps as DataTableSchema,
  HeatmapProps as HeatmapSchema,
  LineChartProps as LineChartSchema,
  SankeyChartProps as SankeyChartSchema,
  ScatterChartProps as ScatterChartSchema,
} from './catalog';
import { formatCellValue } from './cellFormat';
import { useDataUrl } from './useDataUrl';

// Prop types inferred from catalog Zod schemas — single source of truth
type LineChartProps = z.infer<typeof LineChartSchema>;
type BarChartProps = z.infer<typeof BarChartSchema>;
type AreaChartProps = z.infer<typeof AreaChartSchema>;
type ScatterChartProps = z.infer<typeof ScatterChartSchema>;
type DataTableProps = z.infer<typeof DataTableSchema>;
type SankeyChartProps = z.infer<typeof SankeyChartSchema>;
type HeatmapProps = z.infer<typeof HeatmapSchema>;
type ComboChartProps = z.infer<typeof ComboChartSchema>;
type CardProps = z.infer<typeof CardSchema>;

// Chart colors from CSS variables defined in globals.css — auto-switches light/dark mode
const DEFAULT_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-1) / 0.6)',
];

// Theme-aware Recharts tooltip styling using CSS variables from globals.css.
const TOOLTIP_STYLE: React.CSSProperties = {
  background: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  fontSize: 12,
  padding: '6px 10px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)',
};

// Caps tooltip values to 2 decimal places so long floating-point series
// (e.g. computed ratios) don't render with excessive precision.
const TOOLTIP_NUMBER_FORMATTER = (value: number | string) =>
  isNumber(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value;

// ---------------------------------------------------------------------------
// Grid — CSS Grid layout, children auto-fill cells
// Matches upstream shadcnComponents.Grid: { columns: 1-6, gap: sm|md|lg }
// ---------------------------------------------------------------------------

const colsMap: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};
const gapMap: Record<string, string> = { sm: 'gap-2', md: 'gap-3', lg: 'gap-4' };

export const GridComponent = ({
  props,
  children,
}: BaseComponentProps<ShadcnProps<'Grid'>>) => {
  const cols = colsMap[Math.max(1, Math.min(6, props.columns ?? 1))] ?? 'grid-cols-1';
  const gap = gapMap[props.gap ?? 'md'] ?? 'gap-3';
  return <div className={`grid ${cols} ${gap}`}>{children}</div>;
};

// ---------------------------------------------------------------------------
// Stack — flex container (vertical by default, horizontal with direction prop)
// Matches upstream shadcnComponents.Stack: { direction, gap, align, justify }
// ---------------------------------------------------------------------------

const stackGapMap: Record<string, string> = { none: 'gap-0', sm: 'gap-2', md: 'gap-3', lg: 'gap-4' };
const alignMap: Record<string, string> = { start: 'items-start', center: 'items-center', end: 'items-end', stretch: 'items-stretch' };
const justifyMap: Record<string, string> = { start: 'justify-start', center: 'justify-center', end: 'justify-end', between: 'justify-between', around: 'justify-around' };

export const StackComponent = ({
  props,
  children,
}: BaseComponentProps<ShadcnProps<'Stack'>>) => {
  const isHorizontal = props.direction === 'horizontal';
  const gap = stackGapMap[props.gap ?? 'md'] ?? 'gap-3';
  const align = alignMap[props.align ?? 'stretch'] ?? '';
  const justify = justifyMap[props.justify ?? 'start'] ?? '';
  return (
    <div className={`flex ${isHorizontal ? 'flex-row flex-wrap' : 'flex-col'} ${gap} ${align} ${justify}`}>
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Card — stat / container card with three text slots in the header.
//
// Diverges from upstream shadcnComponents.Card by adding an explicit `value`
// prop for the headline metric. The original upstream contract — { title,
// description, maxWidth, centered } — left no slot for "loud" text, so KPI
// specs ended up shoving the metric into `description` and rendering as all
// muted text. The local override fixes that without forcing every KPI Card
// to nest a child Heading element.
//
// Slot layout (top → bottom):
//   props.title       → CardDescription (small, muted-foreground) — caption above
//   props.value       → text-lg + text-foreground (large, dark)   — headline metric
//   props.description → CardDescription (small, muted-foreground) — caption below
//   children          → CardContent body (default slot, optional) — extra content
// ---------------------------------------------------------------------------

const maxWidthMap: Record<string, string> = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', full: 'max-w-full' };

export const CardComponent = ({
  props,
  children,
}: BaseComponentProps<CardProps>) => {
  const maxWidth = maxWidthMap[props.maxWidth ?? 'full'] ?? '';
  const centered = props.centered ? 'mx-auto' : '';
  // React.Children.count handles the empty-array case: `children` arrives as
  // `[]` (truthy) when no body slot was passed, which would otherwise render
  // an empty CardContent and contribute stray p-3 padding.
  const hasChildren = React.Children.count(children) > 0;
  return (
    <Card className={`border-0 shadow-none bg-muted/70 ${maxWidth} ${centered}`}>
      <CardHeader className="p-3 space-y-0.5">
        {/* title → muted caption above the headline (e.g. "Total PnL") */}
        {props.title && <CardDescription>{props.title}</CardDescription>}
        {/* value → large dark headline metric (e.g. "$2,640") */}
        {props.value && <div className="text-lg text-foreground leading-tight font-medium">{props.value}</div>}
        {/* description → muted caption below the headline (e.g. "1 lot · YTD") */}
        {props.description && <CardDescription>{props.description}</CardDescription>}
      </CardHeader>
      {/* default slot — body content rendered below the header text block */}
      {hasChildren && <CardContent className="p-3 pt-0">{children}</CardContent>}
    </Card>
  );
};

// strokeDasharray values for line style variants
const DASH_MAP: Record<string, string | undefined> = {
  solid: undefined,
  dashed: '6 3',
  dotted: '2 2',
};

// Shared axis styling for clean, minimal charts
const AXIS_TICK = { fontSize: 11 };
const AXIS_LINE = { stroke: 'hsl(var(--border))' };
// Faint horizontal-only grid lines matching the reference chart style
const GRID_PROPS = { horizontal: true, vertical: false, stroke: 'hsl(var(--border))', strokeOpacity: 0.5 } as const;
// Legend positioned above chart, left-aligned. cursor:pointer + userSelect:none
// signal the legend is interactive (entries toggle series visibility).
const LEGEND_PROPS = {
  verticalAlign: 'top' as const,
  align: 'left' as const,
  iconType: 'square' as const,
  iconSize: 12,
  wrapperStyle: {
    fontSize: 12,
    color: 'hsl(var(--muted-foreground))',
    paddingBottom: 4,
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
};

// Click a legend entry to toggle that series. Returns the hidden-keys map (pass
// to `hide` on each series) and Legend props (onClick + formatter that dims
// hidden entries). Recharts types `Payload.dataKey` as `DataKey<any>` —
// string | number | ((obj) => any) — so we mirror that and only treat
// primitive keys as identifiers (function dataKeys aren't used here).
type LegendEntry = {
  dataKey?: string | number | ((obj: unknown) => unknown);
  value?: unknown;
};
const keyFromEntry = (entry: LegendEntry): string => {
  const { dataKey, value } = entry;
  if (isString(dataKey) || isNumber(dataKey)) return String(dataKey);
  if (isString(value) || isNumber(value)) return String(value);
  return '';
};
const useLegendToggle = () => {
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const onClick = useCallback((entry: LegendEntry) => {
    const key = keyFromEntry(entry);
    if (!key) return;
    setHidden(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const formatter = useCallback(
    (value: string, entry: LegendEntry) => {
      const key = keyFromEntry(entry);
      const isHidden = !!hidden[key];
      return (
        <span style={{ opacity: isHidden ? 0.4 : 1, textDecoration: isHidden ? 'line-through' : undefined }}>
          {value}
        </span>
      );
    },
    [hidden],
  );
  return { hidden, legendProps: { ...LEGEND_PROPS, onClick, formatter } };
};

// ---------------------------------------------------------------------------
// LineChart
// ---------------------------------------------------------------------------

export const LineChart = ({ props }: BaseComponentProps<LineChartProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as object[];
  const colors = props.colors ?? DEFAULT_COLORS;
  const height = props.height ?? 300;
  // yKeys is required by Zod, but a `{$state:"/path"}` binding can resolve to
  // `undefined` at render time when the path is unset/mistyped — `.length`
  // then throws and unmounts the chart under ElementErrorBoundary. Coerce
  // non-array shapes to [] so the chart renders empty instead of crashing.
  // Regression: scripts/fuzz_genui/results.tsv row 036.
  const yKeys = isArray(props.yKeys) ? props.yKeys : [];
  const { hidden, legendProps } = useLegendToggle();

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart data={data} margin={props.xAxisShowAllTicks ? { bottom: 16, right: 16 } : undefined}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey={props.xKey}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            interval={props.xAxisShowAllTicks ? 0 : undefined}
            // Forcing every tick to render (below) can crowd/collide labels
            // in a narrow chart - e.g. "Nov"/"Dec" running together as
            // "NovDe". Angling them buys enough horizontal room per label
            // without needing a wider chart.
            {...(props.xAxisShowAllTicks ? { angle: -45, textAnchor: 'end', height: 45 } : {})}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            // Recharts' default YAxis domain is [0, 'auto']; override so line
            // charts fit the data range instead of always anchoring to zero.
            // Cast because yDomain is z.unknown()-typed (see catalog.ts).
            domain={(props.yDomain as [number | string, number | string]) ?? ['auto', 'auto']}
            label={
              props.yAxisLabel
                ? { value: props.yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11 }
                : undefined
            }
          />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          {yKeys.length > 1 && (
            <Legend {...legendProps} />
          )}
          {map(yKeys, (key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              strokeDasharray={DASH_MAP[props.lineStyles?.[key] ?? 'solid']}
              dot={false}
              activeDot={{ r: 4 }}
              hide={!!hidden[key]}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BarChart — bar chart
// ---------------------------------------------------------------------------

export const GenUIBarChart = ({ props }: BaseComponentProps<BarChartProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as object[];
  const colors = props.colors ?? DEFAULT_COLORS;
  const height = props.height ?? 300;
  // See LineChart fix in fuzz-036: yKeys can resolve to undefined via $state
  // bindings to missing paths. Coerce to [] so .length and lodash.map are safe.
  // Regression: scripts/fuzz_genui/results.tsv row 040.
  const yKeys = isArray(props.yKeys) ? props.yKeys : [];
  const { hidden, legendProps } = useLegendToggle();

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {/* stackOffset="sign" splits each stack at y=0 so positive and negative
            segments grow in opposite directions. Without it, Recharts' default
            ("none") sums signed values into a single rect — a +200/-100 row
            renders as a single +100 bar and one series visually absorbs the
            other (regression reported by Keshav, Slack 2026-06-26). */}
        <BarChart data={data} stackOffset={props.stacked ? 'sign' : undefined}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey={props.xKey}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            domain={props.yDomain ?? ['auto', 'auto']}
          />
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={TOOLTIP_NUMBER_FORMATTER} />
          {yKeys.length > 1 && (
            <Legend {...legendProps} />
          )}
          {map(yKeys, (key, i) => {
            // Stacked bars: keep corners flat. With diverging stacks the
            // "top" segment depends on each row's sign, so the previous
            // last-yKey-wins heuristic would round the wrong edge.
            const radius: [number, number, number, number] =
              props.stacked ? [0, 0, 0, 0] : [4, 4, 0, 0];
            return (
              <Bar
                key={key}
                dataKey={key}
                fill={colors[i % colors.length]}
                stackId={props.stacked ? 'stack' : undefined}
                radius={radius}
                hide={!!hidden[key]}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AreaChart
// ---------------------------------------------------------------------------

export const GenUIAreaChart = ({ props }: BaseComponentProps<AreaChartProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as object[];
  const colors = props.colors ?? DEFAULT_COLORS;
  const height = props.height ?? 300;
  const useGradient = props.gradient !== false;
  // See LineChart fix in fuzz-036 / BarChart in fuzz-040: yKeys can resolve to
  // undefined via $state bindings to missing paths. Coerce to [].
  // Regression: scripts/fuzz_genui/results.tsv row 040.
  const yKeys = isArray(props.yKeys) ? props.yKeys : [];
  const { hidden, legendProps } = useLegendToggle();

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {/* stackOffset="sign": diverging stack so mixed-sign series remain
            visible. See GenUIBarChart for context. */}
        <RechartsAreaChart data={data} stackOffset={props.stacked ? 'sign' : undefined}>
          {useGradient && (
            <defs>
              {map(yKeys, (key, i) => {
                // Sanitize key for SVG id — spaces/special chars in data keys like
                // "Lote 1AB" make url(#gradient-Lote 1AB) unresolvable. When the
                // browser can't find the gradient, SVG fills default to black,
                // causing the entire chart area to render with a black background.
                const safeId = replace(key, /[^a-zA-Z0-9_-]/g, '_');
                return (
                  <linearGradient key={key} id={`gradient-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors[i % colors.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={colors[i % colors.length]} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
          )}
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey={props.xKey}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            // Recharts' default YAxis domain is [0, 'auto']; override so the
            // area chart fits the data range instead of always anchoring to
            // zero (same fix as GenUILineChart above).
            domain={['auto', 'auto']}
          />
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          {yKeys.length > 1 && (
            <Legend {...legendProps} />
          )}
          {map(yKeys, (key, i) => {
            const safeId = replace(key, /[^a-zA-Z0-9_-]/g, '_');
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[i % colors.length]}
                strokeDasharray={DASH_MAP[props.lineStyles?.[key] ?? 'solid']}
                fill={useGradient ? `url(#gradient-${safeId})` : colors[i % colors.length]}
                fillOpacity={useGradient ? 1 : 0.2}
                stackId={props.stacked ? 'stack' : undefined}
                strokeWidth={2}
                hide={!!hidden[key]}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ScatterChart — scatter/dot plot
// ---------------------------------------------------------------------------

// Recharts' `shape` must return an element; an empty <g/> draws nothing, so a
// series can show only its connecting `line` (regression fits, reference curves).
const NO_MARKER = constant(<g />);
// Translucent points so overplotted regions read as density instead of a
// solid slab; `pointOpacity` overrides per chart.
const DEFAULT_POINT_OPACITY = 0.4;

export const GenUIScatterChart = ({ props }: BaseComponentProps<ScatterChartProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as Record<string, unknown>[];
  const colors = props.colors ?? DEFAULT_COLORS;
  const height = props.height ?? 300;
  // fuzz-075: Zod marks yKeys required, but the chatbot can plausibly emit
  // `yKey` (singular) by mistake or omit it on an empty-data placeholder.
  // Mapping over an undefined value crashed the chart and the parent error
  // boundary. Coerce missing/non-array to [].
  const yKeys = isArray(props.yKeys) ? props.yKeys : [];
  const lineKeys = isArray(props.lineKeys) ? props.lineKeys : [];
  const { hidden, legendProps } = useLegendToggle();

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsScatterChart>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey={props.xKey}
            type="number"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            name={props.xKey}
            // Recharts defaults numeric axes to [0, 'auto']; a price-vs-spread
            // scatter then wastes the left half of the chart on empty space
            // between 0 and the first observation.
            domain={['auto', 'auto']}
          />
          {/*
            No `dataKey` on the YAxis: Recharts then resolves each point's y
            from its own <Scatter dataKey>, computes the axis domain as the
            union over all series, and labels the tooltip's y entry with the
            series `name`. A YAxis dataKey would pin every series to one key.
          */}
          <YAxis
            type="number"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            domain={['auto', 'auto']}
          />
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={TOOLTIP_NUMBER_FORMATTER} />
          {yKeys.length > 1 && (
            <Legend {...legendProps} />
          )}
          {map(yKeys, (key, i) => {
            const isLine = includes(lineKeys, key);
            // Each series gets only the rows where its key is populated, so
            // wide-format data (one populated y-key per row) plots each row
            // exactly once, under its own series' colour. Line series are
            // sorted by x so the connecting path doesn't zig-zag.
            const rows = filter(data, row => isObject(row) && !isNil(row[key]));
            const color = colors[i % colors.length];
            return (
              <Scatter
                key={key}
                name={key}
                dataKey={key}
                data={isLine ? sortBy(rows, props.xKey) : rows}
                fill={color}
                fillOpacity={isLine ? 1 : isNumber(props.pointOpacity) ? props.pointOpacity : DEFAULT_POINT_OPACITY}
                line={isLine ? { stroke: color, strokeWidth: 2 } : false}
                shape={isLine ? NO_MARKER : 'circle'}
                hide={!!hidden[key]}
              />
            );
          })}
        </RechartsScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export const DataTable = ({ props }: BaseComponentProps<DataTableProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as Record<string, unknown>[];
  // Zod types columns as an array, but at runtime $state refs can resolve to
  // any shape (object, string, number). lodash.map on an object iterates its
  // values — none of which will have .key/.label, producing blank headers and
  // React key warnings. Coerce to [] so the card renders empty but valid.
  // Regression: scripts/fuzz_genui/results.tsv row 026.
  const columns = isArray(props.columns) ? props.columns : [];

  return (
    <div className="flex flex-col gap-1">
      {props.title && (
        <div className="text-sm font-semibold text-foreground">{props.title}</div>
      )}
      <div className="overflow-auto max-h-[400px]">
        <Table>
          <TableHeader className="bg-background sticky top-0 z-10">
            <TableRow className="h-7">
              {map(columns, (col, colIndex) => {
                // Zod types label as string, but at runtime $state refs can resolve to
                // objects/arrays. React throws "Objects are not valid as a React child"
                // when an object label hits the DOM. Coerce to string (or fall back to key).
                // Regression: scripts/fuzz_genui/results.tsv row 083.
                const label = isString(col.label) ? col.label : (col.key ?? '');
                const isLastColumn = props.boldLastColumn && colIndex === columns.length - 1;
                return (
                  <TableHead
                    key={col.key}
                    className={`py-1 px-2 text-xs whitespace-nowrap ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${isLastColumn ? 'font-bold border-l' : ''}`}
                  >
                    {label}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {map(data, (row, i) => {
              const isLastRow = props.boldLastRow && i === data.length - 1;
              return (
                <TableRow key={i} className={`h-7 ${isLastRow ? 'font-bold border-t-2' : ''}`}>
                  {map(columns, (col, colIndex) => {
                    const isLastColumn = props.boldLastColumn && colIndex === columns.length - 1;
                    return (
                      <TableCell
                        key={col.key}
                        className={`py-1 px-2 text-xs ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${isLastColumn ? 'font-bold border-l' : ''}`}
                      >
                        {/* LLM-emitted data arrays can contain null/undefined
                            rows — useDataUrl guarantees an array, not element
                            shape. Regression: fuzz + Sentry REACT-ES. */}
                        {formatCellValue(row?.[col.key])}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToggleGroup — segmented button group bound to state
// Upstream API uses `items` (not `options`) and `type` (single|multiple)
// ---------------------------------------------------------------------------

export const GenUIToggleGroup = ({ props, bindings, emit }: BaseComponentProps<ShadcnProps<'ToggleGroup'>>) => {
  // Upstream schema uses nullable (string | null); coerce to undefined for useBoundProp
  const [value, setValue] = useBoundProp<string>(props.value ?? undefined, bindings?.value);

  return (
    <div className="flex flex-col gap-1.5">
      <ToggleGroup
        // Cast needed: Radix ToggleGroup has a discriminated union on `type`,
        // but we always treat the value as a single string via useBoundProp
        type={(props.type ?? 'single') as 'single'}
        value={value ?? ''}
        onValueChange={(v) => {
          // Radix ToggleGroup sends empty string on deselect — ignore it
          if (v) {
            setValue(v);
            emit('change');
          }
        }}
        className="justify-start"
      >
        {map(props.items, (item) => (
          <ToggleGroupItem key={item.value} value={item.value} size="sm" className="h-6 text-xs">
            {item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Slider — numeric slider bound to state
// Matches upstream shadcnComponents.Slider
// ---------------------------------------------------------------------------

export const GenUISlider = ({ props, bindings, emit }: BaseComponentProps<ShadcnProps<'Slider'>>) => {
  // Upstream schema uses nullable (number | null); coerce to undefined for useBoundProp
  const [value, setValue] = useBoundProp<number>(props.value ?? undefined, bindings?.value);
  const min = props.min ?? 0;
  const max = props.max ?? 100;
  const step = props.step ?? 1;

  return (
    <div className="flex flex-col gap-1.5 min-w-[140px]">
      {props.label && (
        <Label className="text-xs text-muted-foreground">
          {props.label}: {value ?? min}
        </Label>
      )}
      <Slider
        value={[value ?? min]}
        onValueChange={([v]) => {
          setValue(v);
          emit('change');
        }}
        min={min}
        max={max}
        step={step}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Select — dropdown bound to state
// Upstream API uses `options: string[]` (not `{value, label}[]`), with `name` and `label` required
// ---------------------------------------------------------------------------

export const GenUISelect = ({ props, bindings, emit }: BaseComponentProps<ShadcnProps<'Select'>>) => {
  // Upstream schema uses nullable (string | null); coerce to undefined for useBoundProp
  const [value, setValue] = useBoundProp<string>(props.value ?? undefined, bindings?.value);

  return (
    <div className="flex flex-col gap-1.5">
      {props.label && (
        <Label className="text-xs text-muted-foreground">{props.label}</Label>
      )}
      <Select
        value={value ?? ''}
        onValueChange={(v) => {
          setValue(v);
          emit('change');
        }}
      >
        <SelectTrigger size="sm" className="w-[160px] !h-6 py-0 text-xs">
          <SelectValue placeholder={props.placeholder ?? 'Select...'} />
        </SelectTrigger>
        <SelectContent>
          {/* Zod types options as string[], but state-bound props can resolve to
            * objects/nested arrays. Rendering an object as a React child throws
            * "Objects are not valid as a React child". Filter to strings; map
            * {label,value} to value (so SelectValue still works as expected).
            * Regression: scripts/fuzz_genui/results.tsv row 085. */}
          {map(
            isArray(props.options) ? props.options : [],
            (opt, i) => {
              if (isString(opt)) {
                return (
                  <SelectItem key={`${opt}-${i}`} value={opt}>
                    {opt}
                  </SelectItem>
                );
              }
              if (!isNil(opt) && isObject(opt) && !isArray(opt)) {
                const v = (opt as { value?: unknown }).value;
                const l = (opt as { label?: unknown }).label;
                if (isString(v)) {
                  const display = isString(l) ? l : v;
                  return (
                    <SelectItem key={`${v}-${i}`} value={v}>
                      {display}
                    </SelectItem>
                  );
                }
              }
              return null;
            },
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GenUI Tabs — tab navigation with state binding
// ---------------------------------------------------------------------------

export const GenUITabs = ({
  props,
  children,
  bindings,
  emit,
}: BaseComponentProps<ShadcnProps<'Tabs'>>) => {
  const [value, setValue] = useBoundProp<string>(props.value ?? props.defaultValue ?? props.tabs[0]?.value, bindings?.value);

  const handleChange = (v: string) => {
    setValue(v);
    emit('change');
  };

  return (
    <Tabs value={value ?? props.tabs[0]?.value} onValueChange={handleChange}>
      <TabsList className="h-7 mb-2">
        {map(props.tabs, (tab) => (
          <TabsTrigger key={tab.value} value={tab.value} className="py-1 text-xs">
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {/* Each tab panel is keyed by tab value; children slot renders inside each */}
      {map(props.tabs, (tab) => (
        <TabsContent key={tab.value} value={tab.value} className="mt-1">
          {tab.value === value ? children : null}
        </TabsContent>
      ))}
    </Tabs>
  );
};

// ---------------------------------------------------------------------------
// GenUI Accordion — collapsible sections
// ---------------------------------------------------------------------------

export const GenUIAccordion = ({
  props,
}: BaseComponentProps<ShadcnProps<'Accordion'>>) => {
  return (
    <Accordion type={props.type ?? 'single'} collapsible>
      {map(props.items, (item, i) => (
        <AccordionItem key={i} value={`item-${i}`}>
          <AccordionTrigger className="py-2 text-sm">{item.title}</AccordionTrigger>
          <AccordionContent className="pb-2">{item.content}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

// ---------------------------------------------------------------------------
// GenUI Collapsible — single collapsible with rich children
// ---------------------------------------------------------------------------

export const GenUICollapsible = ({
  props,
  children,
}: BaseComponentProps<ShadcnProps<'Collapsible'>>) => {
  return (
    <Collapsible defaultOpen={props.defaultOpen ?? false}>
      <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:underline">
        {props.title}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// GenUI Text — narrative paragraphs
// ---------------------------------------------------------------------------

const textVariantClasses: Record<string, string> = {
  body: 'text-sm',
  caption: 'text-xs text-muted-foreground',
  muted: 'text-muted-foreground',
  lead: 'text-lg',
  code: 'font-mono text-sm bg-muted p-1.5 rounded',
};

export const GenUIText = ({ props }: BaseComponentProps<ShadcnProps<'Text'>>) => {
  const className = textVariantClasses[props.variant ?? 'body'] ?? textVariantClasses.body;
  return <div className={className}>{props.text}</div>;
};

// ---------------------------------------------------------------------------
// GenUI Heading — section headings (h1-h4)
// ---------------------------------------------------------------------------

const headingClasses: Record<string, string> = {
  h1: 'text-2xl font-semibold tracking-tight',
  h2: 'text-lg font-semibold tracking-tight',
  h3: 'text-base font-semibold',
  h4: 'text-sm font-semibold',
};

export const GenUIHeading = ({ props }: BaseComponentProps<ShadcnProps<'Heading'>>) => {
  const level = props.level ?? 'h2';
  const className = headingClasses[level] ?? headingClasses.h2;
  switch (level) {
  case 'h1': return <h1 className={className}>{props.text}</h1>;
  case 'h3': return <h3 className={className}>{props.text}</h3>;
  case 'h4': return <h4 className={className}>{props.text}</h4>;
  default: return <h2 className={className}>{props.text}</h2>;
  }
};

// ---------------------------------------------------------------------------
// GenUI Badge — status indicator
// ---------------------------------------------------------------------------

export const GenUIBadge = ({ props }: BaseComponentProps<ShadcnProps<'Badge'>>) => {
  return <Badge variant={props.variant ?? 'default'}>{props.text}</Badge>;
};

// ---------------------------------------------------------------------------
// GenUI Alert — informational banners with icons
// ---------------------------------------------------------------------------

const alertIcons: Record<string, React.ReactNode> = {
  info: <Info className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  error: <XCircle className="h-4 w-4" />,
  success: <CheckCircle className="h-4 w-4" />,
};

export const GenUIAlert = ({ props }: BaseComponentProps<ShadcnProps<'Alert'>>) => {
  const type = props.type ?? 'info';
  // Map alert types to shadcn Alert variants
  const variant = type === 'error' || type === 'warning' ? 'destructive' : 'default';

  return (
    <Alert variant={variant} className="px-3 py-2">
      {alertIcons[type]}
      <AlertTitle className="mb-0.5">{props.title}</AlertTitle>
      {props.message && <AlertDescription>{props.message}</AlertDescription>}
    </Alert>
  );
};

// ---------------------------------------------------------------------------
// GenUI Progress — progress/capacity bar
// ---------------------------------------------------------------------------

export const GenUIProgress = ({ props }: BaseComponentProps<ShadcnProps<'Progress'>>) => {
  const max = props.max ?? 100;
  // Normalize to 0-100 percentage for the Radix Progress component
  const percentage = max > 0 ? (props.value / max) * 100 : 0;

  return (
    <div className="flex flex-col gap-1.5">
      {props.label && (
        <Label className="text-xs text-muted-foreground">
          {props.label}: {props.value}/{max}
        </Label>
      )}
      <Progress value={percentage} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// SankeyChart — flow/allocation diagram using Recharts native Sankey
// Recharts Sankey is NOT a categorical chart — it takes width/height directly
// rather than using ResponsiveContainer. We measure the container width with
// a ref and ResizeObserver.
// ---------------------------------------------------------------------------

export const GenUISankeyChart = ({ props }: BaseComponentProps<SankeyChartProps>) => {
  // Sankey data is an object ({ nodes, links }), not an array, so we can't
  // use useDataUrl which is typed for unknown[]. Inline data only for now;
  // dataUrl support can be added later with a dedicated hook.
  const rawData = props.data;
  const height = props.height ?? 400;
  const colors = props.colors ?? DEFAULT_COLORS;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(600);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!rawData?.nodes?.length) return null;

  // Custom node renderer to apply palette colors based on node index
  const renderNode = (nodeProps: Record<string, unknown>) => {
    const { x, y, width: w, height: h, index } = nodeProps as {
      x: number; y: number; width: number; height: number; index: number;
    };
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={colors[(index as number) % colors.length]}
        rx={2}
      />
    );
  };

  return (
    <div ref={containerRef}>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <RechartsSankey
        width={width}
        height={height}
        data={rawData}
        nodePadding={props.nodePadding ?? 24}
        nodeWidth={props.nodeWidth ?? 10}
        node={renderNode}
        linkCurvature={0.5}
        margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
      >
        <Tooltip contentStyle={TOOLTIP_STYLE} />
      </RechartsSankey>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Heatmap — custom SVG grid for matrix visualization
// Pivots flat data into rows x columns with color-coded cells.
// ---------------------------------------------------------------------------

// Color interpolation helpers for heatmap
const HEATMAP_SCALES: Record<string, { from: [number, number, number]; to: [number, number, number] }> = {
  blues: { from: [219, 234, 254], to: [30, 64, 175] },
  reds: { from: [254, 226, 226], to: [185, 28, 28] },
  greens: { from: [220, 252, 231], to: [21, 128, 61] },
  oranges: { from: [255, 237, 213], to: [194, 65, 12] },
};

function interpolateColor (t: number, from: [number, number, number], to: [number, number, number]): string {
  const r = Math.round(from[0] + (to[0] - from[0]) * t);
  const g = Math.round(from[1] + (to[1] - from[1]) * t);
  const b = Math.round(from[2] + (to[2] - from[2]) * t);
  return `rgb(${r},${g},${b})`;
}

function heatmapColor (value: number, min: number, max: number, scale: string): string {
  const range = max - min;
  const t = range === 0 ? 0.5 : (value - min) / range;

  if (scale === 'diverging') {
    // Blue (negative) → white (zero) → red (positive)
    if (t < 0.5) {
      return interpolateColor(1 - t * 2, [255, 255, 255], [30, 64, 175]);
    }
    return interpolateColor((t - 0.5) * 2, [255, 255, 255], [185, 28, 28]);
  }

  const { from, to } = HEATMAP_SCALES[scale] ?? HEATMAP_SCALES.blues;
  return interpolateColor(t, from, to);
}

export const GenUIHeatmap = ({ props }: BaseComponentProps<HeatmapProps>) => {
  // Cast: props.data is z.unknown() in the schema (TS 4.9 + Zod v4 limitation),
  // but at runtime it's either Array<Record> or number[][] — see HeatmapProps.
  const rawData = useDataUrl(props.dataUrl, props.data as unknown[] | undefined) as unknown[];
  const scale = props.colorScale ?? 'blues';
  const showValues = props.showValues !== false;

  if (!rawData?.length) return null;

  // Detect matrix form: data is number[][] aligned to xLabels (cols) x yLabels (rows).
  // Some LLMs emit this shape instead of the long form, so accept both.
  const isMatrixForm = isArray(rawData[0]);

  // Extract unique x and y values preserving order of appearance
  const xValues: string[] = [];
  const yValues: string[] = [];
  const cellMap = new Map<string, number>();
  let minVal = Infinity;
  let maxVal = -Infinity;

  if (isMatrixForm) {
    const matrix = rawData as (number | null)[][];
    const xLabels = props.xLabels ?? map(matrix[0] ?? [], (_, i) => String(i));
    const yLabels = props.yLabels ?? map(matrix, (_, i) => String(i));
    xValues.push(...xLabels);
    yValues.push(...yLabels);
    for (let yi = 0; yi < yLabels.length; yi += 1) {
      // fuzz-079: matrix rows can be null/string/object/missing in LLM
      // output. Reading row[xi] on null throws "Cannot read properties of
      // null". Coerce non-array rows to [] so missing cells become NaN
      // (gray fallback) instead of crashing the chart.
      const rawRow = matrix[yi];
      const row = isArray(rawRow) ? rawRow : [];
      for (let xi = 0; xi < xLabels.length; xi += 1) {
        const v = Number(row[xi]);
        cellMap.set(`${yLabels[yi]}__${xLabels[xi]}`, v);
        if (!isNaN(v)) {
          minVal = Math.min(minVal, v);
          maxVal = Math.max(maxVal, v);
        }
      }
    }
  } else {
    const data = rawData as Record<string, unknown>[];
    for (const row of data) {
      // fuzz-079: long-form rows can be null in pathological LLM output.
      // null[key] throws "Cannot read properties of null". Skip rather
      // than coerce; a null row carries no x/y/value meaning.
      if (isNil(row) || !isObject(row)) continue;
      // fuzz-092: row[xKey]/row[yKey] can be objects/arrays. String({}) yields
      // "[object Object]", which renders literally as an axis label and trips
      // the nan_leak detector. Only accept primitives; skip rows whose axis
      // dimension isn't a string/number/bool.
      const xRaw = row[props.xKey ?? ''];
      const yRaw = row[props.yKey ?? ''];
      if (!isNil(xRaw) && isObject(xRaw)) continue;
      if (!isNil(yRaw) && isObject(yRaw)) continue;
      const x = String(xRaw ?? '');
      const y = String(yRaw ?? '');
      const v = Number(row[props.valueKey ?? '']);
      if (!includes(xValues, x)) xValues.push(x);
      if (!includes(yValues, y)) yValues.push(y);
      cellMap.set(`${y}__${x}`, v);
      if (!isNaN(v)) {
        minVal = Math.min(minVal, v);
        maxVal = Math.max(maxVal, v);
      }
    }
  }

  // Layout constants
  const yLabelWidth = 80;
  const cellPad = 1;
  const cellWidth = 50;
  const availableWidth = '100%';

  const axisFontSize = 11;

  // 45°-rotated x-axis labels need vertical space ≈ label_width * sin(45°).
  // 0.55 approximates the average glyph-width / font-size ratio for the
  // default sans-serif family; we use it to size the label slot to fit the
  // longest label regardless of how tall or long individual labels are.
  const CHAR_W_RATIO = 0.55;
  const xMaxLen = Math.max(1, ...map(xValues, v => v.length));
  const xLabelHeight = Math.ceil(xMaxLen * axisFontSize * CHAR_W_RATIO * Math.SQRT1_2) + 10;

  // viewBox dimensions are grid-proportional; default the rendered height to
  // match viewBox height so fonts/cells render at their native pixel sizes
  // instead of being stretched when the container is taller than the grid
  // needs (was previously a fixed 300, which blew up cell numbers in narrow
  // artifact panels once preserveAspectRatio="meet" kicked in).
  const viewBoxHeight = xLabelHeight + yValues.length * 30 + 8;
  const height = props.height ?? viewBoxHeight;

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <svg width={availableWidth} height={height} viewBox={`0 0 ${yLabelWidth + xValues.length * cellWidth} ${viewBoxHeight}`} className="overflow-visible">
        {/* X-axis labels — rotated -45° so long names don't overflow their
            column slot. Anchor sits at the top of each column and the label
            rises up-right from there. */}
        {map(xValues, (x, xi) => {
          const cx = yLabelWidth + xi * cellWidth + cellWidth / 2;
          const cy = xLabelHeight - 4;
          return (
            <text
              key={`xl-${x}`}
              x={cx}
              y={cy}
              textAnchor="start"
              className="fill-muted-foreground"
              fontSize={axisFontSize}
              transform={`rotate(-45 ${cx} ${cy})`}
            >
              {x}
            </text>
          );
        })}
        {/* Rows */}
        {map(yValues, (y, yi) => (
          <g key={`row-${y}`}>
            {/* Y-axis label */}
            <text
              x={yLabelWidth - 6}
              y={xLabelHeight + yi * 30 + 18}
              textAnchor="end"
              className="fill-muted-foreground"
              fontSize={axisFontSize}
            >
              {y}
            </text>
            {/* Cells */}
            {map(xValues, (x, xi) => {
              const val = cellMap.get(`${y}__${x}`);
              const color = !isNil(val) && !isNaN(val) ? heatmapColor(val, minVal, maxVal, scale) : 'hsl(var(--muted))';
              // Pick text color based on cell brightness for readability
              const t = !isNil(val) && !isNaN(val) && maxVal !== minVal ? (val - minVal) / (maxVal - minVal) : 0.5;
              const textColor = scale === 'diverging' ? (t > 0.3 && t < 0.7 ? '#111' : '#fff') : (t > 0.5 ? '#fff' : '#111');
              return (
                <g key={`cell-${y}-${x}`}>
                  <rect
                    x={yLabelWidth + xi * 50 + cellPad}
                    y={xLabelHeight + yi * 30 + cellPad}
                    width={50 - cellPad * 2}
                    height={30 - cellPad * 2}
                    fill={color}
                    rx={3}
                  >
                    <title>{`${y} / ${x}: ${val ?? '–'}`}</title>
                  </rect>
                  {showValues && !isNil(val) && !isNaN(val) && (
                    <text
                      x={yLabelWidth + xi * 50 + 25}
                      y={xLabelHeight + yi * 30 + 18}
                      textAnchor="middle"
                      fontSize={11}
                      fill={textColor}
                    >
                      {Number.isInteger(val) ? val : val.toFixed(2)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ComboChart — combined bar + line chart using Recharts ComposedChart
// ---------------------------------------------------------------------------

export const GenUIComboChart = ({ props }: BaseComponentProps<ComboChartProps>) => {
  const data = useDataUrl(props.dataUrl, props.data) as object[];
  const barColors = props.barColors ?? DEFAULT_COLORS;
  const lineColors = props.lineColors ?? DEFAULT_COLORS.slice(3);
  const height = props.height ?? 300;
  const rightAxisKeys = new Set(props.rightAxisKeys ?? []);
  const hasRightAxis = rightAxisKeys.size > 0;
  // fuzz-076: barKeys/lineKeys are Zod-required but live LLM output omits
  // them or emits a `series` array instead. Reading .length on undefined
  // crashed the chart with "Cannot read properties of undefined (reading
  // 'length')". Mirror iter-075 ScatterChart pattern: lodash isArray coerce.
  const barKeys = isArray(props.barKeys) ? props.barKeys : [];
  const lineKeys = isArray(props.lineKeys) ? props.lineKeys : [];
  const { hidden, legendProps } = useLegendToggle();

  return (
    <div>
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {/* stackOffset="sign": diverging stack so mixed-sign bars remain
            visible. See GenUIBarChart for context. */}
        <ComposedChart data={data} stackOffset={props.stacked ? 'sign' : undefined}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis
            dataKey={props.xKey}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
          />
          <YAxis
            yAxisId="left"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={AXIS_LINE}
            className="fill-muted-foreground"
            label={
              props.yAxisLabel
                ? { value: props.yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11 }
                : undefined
            }
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={AXIS_LINE}
              className="fill-muted-foreground"
            />
          )}
          <Tooltip contentStyle={TOOLTIP_STYLE} />
          {(barKeys.length + lineKeys.length > 1) && (
            <Legend {...legendProps} />
          )}
          {map(barKeys, (key, i) => {
            // Stacked bars: flat corners. Diverging stacks can flip which
            // segment is on top per row, so the prior last-yKey-wins
            // heuristic would round the wrong edge.
            const radius: [number, number, number, number] =
              props.stacked ? [0, 0, 0, 0] : [4, 4, 0, 0];
            return (
              <Bar
                key={key}
                dataKey={key}
                yAxisId={rightAxisKeys.has(key) ? 'right' : 'left'}
                fill={barColors[i % barColors.length]}
                stackId={props.stacked ? 'stack' : undefined}
                radius={radius}
                hide={!!hidden[key]}
              />
            );
          })}
          {map(lineKeys, (key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              yAxisId={rightAxisKeys.has(key) ? 'right' : 'left'}
              stroke={lineColors[i % lineColors.length]}
              strokeWidth={2}
              dot={{ r: 3, fill: lineColors[i % lineColors.length], strokeWidth: 0 }}
              activeDot={{ r: 4 }}
              hide={!!hidden[key]}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
